import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { env } from "@/lib/environment";

interface FeedbackFormData {
  type: string;
  priority: string;
  subject: string;
  description: string;
  stepsToReproduce?: string;
  expectedBehavior?: string;
  actualBehavior?: string;
  email?: string;
  page?: string;
  browserInfo?: string;
  screenshots?: FileList | null;
}

interface RatingData {
  overallRating: number;
  easeOfUse: number;
  features: number;
  performance: number;
  design: number;
  comments: string;
}

interface FeedbackHistory {
  rating?: {
    overall_rating: number;
    ease_of_use?: number;
    features?: number;
    performance?: number;
    design?: number;
    comments?: string;
    created_at: string;
  };
  feedback?: Array<{
    id: number;
    type: string;
    priority: string;
    status: string;
    subject: string;
    description: string;
    steps_to_reproduce?: string;
    page_url?: string;
    screenshot_urls?: string[];
    created_at: string;
  }>;
}

const FEEDBACK_TYPES = [
  { value: "bug", label: "🐛 Bug Report" },
  { value: "feature", label: "💡 Feature Request" },
  { value: "improvement", label: "📈 Improvement Suggestion" },
  { value: "ui", label: "🎨 UI/UX Feedback" },
  { value: "performance", label: "⚡ Performance Issue" },
  { value: "data", label: "📊 Data Accuracy Issue" },
  { value: "general", label: "💬 General Feedback" },
];

const PRIORITY_LEVELS = [
  { value: "low", label: "Low - Minor issue" },
  { value: "medium", label: "Medium - Affects workflow" },
  { value: "high", label: "High - Significant impact" },
  { value: "critical", label: "Critical - Blocks usage" },
];

export default function Feedback() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("feedback");
  
  // Feedback form state
  const [feedbackData, setFeedbackData] = useState<FeedbackFormData>({
    type: "",
    priority: "medium",
    subject: "",
    description: "",
    stepsToReproduce: "",
    expectedBehavior: "",
    actualBehavior: "",
    email: "",
    page: window.location.pathname,
    browserInfo: `${navigator.userAgent} | Screen: ${window.screen.width}x${window.screen.height}`,
    screenshots: null,
  });

  // Rating state
  const [ratingData, setRatingData] = useState<RatingData>({
    overallRating: 0,
    easeOfUse: 0,
    features: 0,
    performance: 0,
    design: 0,
    comments: "",
  });

  // Fetch user's feedback history
  const { data: feedbackHistory, isLoading: isLoadingHistory } = useQuery<FeedbackHistory>({
    queryKey: ["/api/feedback/"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/feedback/");
      if (!response.ok) {
        throw new Error("Failed to fetch feedback history");
      }
      return response.json();
    },
    retry: 1,
  });

  // Submit feedback mutation
  const submitFeedbackMutation = useMutation({
    mutationFn: async (data: FeedbackFormData) => {
      // Check if files are attached
      if (data.screenshots && data.screenshots.length > 0) {
        // Use FormData for file uploads with proper authentication
        const formData = new FormData();
        formData.append('type', data.type);
        formData.append('priority', data.priority);
        formData.append('subject', data.subject);
        formData.append('description', data.description);
        if (data.stepsToReproduce) formData.append('stepsToReproduce', data.stepsToReproduce);
        if (data.expectedBehavior) formData.append('expectedBehavior', data.expectedBehavior);
        if (data.actualBehavior) formData.append('actualBehavior', data.actualBehavior);
        if (data.email) formData.append('email', data.email);
        if (data.page) formData.append('page', data.page);
        if (data.browserInfo) formData.append('browserInfo', data.browserInfo);
        
        // Add screenshot files
        for (let i = 0; i < data.screenshots.length; i++) {
          formData.append('screenshots', data.screenshots[i]);
        }
        
        // Use the same authentication method as other API calls
        const token = localStorage.getItem('sessionId');
        const response = await fetch(`${env.apiUrl}/api/feedback/with-files`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formData,
        });
        
        if (!response.ok) {
          const errorData = await response.text();
          throw new Error(`Failed to submit feedback with screenshots: ${response.status} ${errorData}`);
        }
        
        return response.json();
      } else {
        // Use regular JSON endpoint for feedback without files
        const response = await apiRequest("POST", "/api/feedback/", data);
        return response.json();
      }
    },
    onSuccess: () => {
      toast({
        title: "Feedback Submitted",
        description: "Thank you for your feedback! We'll review it and get back to you if needed.",
      });
      
      // Invalidate feedback history to refresh the list
      queryClient.invalidateQueries({ queryKey: ["/api/feedback/"] });
      
      // Switch to history tab to show the new submission
      setActiveTab("history");
      
      // Reset form
      setFeedbackData({
        type: "",
        priority: "medium",
        subject: "",
        description: "",
        stepsToReproduce: "",
        expectedBehavior: "",
        actualBehavior: "",
        email: "",
        page: window.location.pathname,
        browserInfo: feedbackData.browserInfo,
        screenshots: null,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Submission Failed",
        description: error.message || "Failed to submit feedback. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Submit rating mutation
  const submitRatingMutation = useMutation({
    mutationFn: async (data: RatingData) => {
      const response = await apiRequest("POST", "/api/feedback/rating", data);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Rating Submitted",
        description: "Thank you for rating our app! Your feedback helps us improve.",
      });
      
      // Invalidate feedback history to refresh the list
      queryClient.invalidateQueries({ queryKey: ["/api/feedback/"] });
      
      // Switch to history tab to show the new rating
      setActiveTab("history");
      
      // Reset form
      setRatingData({
        overallRating: 0,
        easeOfUse: 0,
        features: 0,
        performance: 0,
        design: 0,
        comments: "",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Submission Failed",
        description: error.message || "Failed to submit rating. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleFeedbackSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!feedbackData.type || !feedbackData.subject || !feedbackData.description) {
      toast({
        title: "Required Fields Missing",
        description: "Please fill in the type, subject, and description fields.",
        variant: "destructive",
      });
      return;
    }
    submitFeedbackMutation.mutate(feedbackData);
  };

  const handleRatingSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (ratingData.overallRating === 0) {
      toast({
        title: "Rating Required",
        description: "Please provide at least an overall rating.",
        variant: "destructive",
      });
      return;
    }
    submitRatingMutation.mutate(ratingData);
  };

  const renderStarRating = (value: number, onChange: (rating: number) => void, size: "sm" | "lg" = "sm") => {
    const starSize = size === "lg" ? "text-2xl" : "text-lg";
    
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            className={`${starSize} transition-colors ${
              star <= value
                ? "text-yellow-400 hover:text-yellow-500"
                : "text-neutral-300 hover:text-yellow-300"
            }`}
          >
            {star <= value ? "⭐" : "☆"}
          </button>
        ))}
        <span className="ml-2 text-sm text-neutral-600">
          {value > 0 ? `${value}/5` : "Not rated"}
        </span>
      </div>
    );
  };

  const isBugReport = feedbackData.type === "bug";

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8 max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-neutral-900">Feedback & Support</h1>
        <p className="text-neutral-600 mt-2">
          Help us improve Bloom by sharing your feedback, reporting bugs, or rating your experience.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-1 lg:grid-cols-4 h-auto">
          <TabsTrigger value="feedback" className="text-xs sm:text-sm">
            Report Issue
          </TabsTrigger>
          <TabsTrigger value="rating" className="text-xs sm:text-sm">
            Rate App
          </TabsTrigger>
          <TabsTrigger value="contact" className="text-xs sm:text-sm">
            Contact
          </TabsTrigger>
          <TabsTrigger value="history" className="text-xs sm:text-sm">
            History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="feedback" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Submit Feedback</CardTitle>
              <CardDescription>
                Report bugs, request features, or share your thoughts on improving Bloom.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleFeedbackSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="feedback-type">Type of Feedback *</Label>
                    <Select 
                      value={feedbackData.type} 
                      onValueChange={(value) => setFeedbackData(prev => ({ ...prev, type: value }))}
                    >
                      <SelectTrigger id="feedback-type">
                        <SelectValue placeholder="Select feedback type" />
                      </SelectTrigger>
                      <SelectContent>
                        {FEEDBACK_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="priority">Priority Level</Label>
                    <Select 
                      value={feedbackData.priority} 
                      onValueChange={(value) => setFeedbackData(prev => ({ ...prev, priority: value }))}
                    >
                      <SelectTrigger id="priority">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PRIORITY_LEVELS.map((priority) => (
                          <SelectItem key={priority.value} value={priority.value}>
                            {priority.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="subject">Subject *</Label>
                  <Input
                    id="subject"
                    placeholder="Brief summary of your feedback"
                    value={feedbackData.subject}
                    onChange={(e) => setFeedbackData(prev => ({ ...prev, subject: e.target.value }))}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    placeholder="Provide a detailed description of your feedback"
                    value={feedbackData.description}
                    onChange={(e) => setFeedbackData(prev => ({ ...prev, description: e.target.value }))}
                    rows={4}
                    required
                  />
                </div>

                {isBugReport && (
                  <>
                    <Separator />
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium text-neutral-900">Bug Report Details</h3>
                      
                      <div>
                        <Label htmlFor="steps">Steps to Reproduce</Label>
                        <Textarea
                          id="steps"
                          placeholder="1. Go to...&#10;2. Click on...&#10;3. See error..."
                          value={feedbackData.stepsToReproduce}
                          onChange={(e) => setFeedbackData(prev => ({ ...prev, stepsToReproduce: e.target.value }))}
                          rows={3}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="expected">Expected Behavior</Label>
                          <Textarea
                            id="expected"
                            placeholder="What should have happened?"
                            value={feedbackData.expectedBehavior}
                            onChange={(e) => setFeedbackData(prev => ({ ...prev, expectedBehavior: e.target.value }))}
                            rows={2}
                          />
                        </div>

                        <div>
                          <Label htmlFor="actual">Actual Behavior</Label>
                          <Textarea
                            id="actual"
                            placeholder="What actually happened?"
                            value={feedbackData.actualBehavior}
                            onChange={(e) => setFeedbackData(prev => ({ ...prev, actualBehavior: e.target.value }))}
                            rows={2}
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}

                <Separator />

                <div>
                  <Label htmlFor="screenshots">Screenshots (Optional)</Label>
                  <Input
                    id="screenshots"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => setFeedbackData(prev => ({ ...prev, screenshots: e.target.files }))}
                    className="cursor-pointer"
                  />
                  <p className="text-sm text-neutral-500 mt-1">
                    Upload screenshots to help us understand the issue better. Multiple files supported.
                  </p>
                  {feedbackData.screenshots && feedbackData.screenshots.length > 0 && (
                    <div className="mt-2 text-sm text-green-600">
                      📎 {feedbackData.screenshots.length} file(s) selected
                    </div>
                  )}
                </div>

                <div>
                  <Label htmlFor="email">Email (Optional)</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your.email@example.com"
                    value={feedbackData.email}
                    onChange={(e) => setFeedbackData(prev => ({ ...prev, email: e.target.value }))}
                  />
                  <p className="text-sm text-neutral-500 mt-1">
                    Provide your email if you'd like us to follow up on your feedback.
                  </p>
                </div>

                <div className="bg-neutral-50 p-4 rounded-lg">
                  <h4 className="font-medium text-neutral-900 mb-2">Technical Information</h4>
                  <div className="text-sm text-neutral-600 space-y-1">
                    <p><strong>Page:</strong> {feedbackData.page}</p>
                    <p><strong>Browser:</strong> {navigator.userAgent.split(' ')[0]} {navigator.userAgent.split(' ')[1]}</p>
                    <p><strong>Screen:</strong> {window.screen.width}x{window.screen.height}</p>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={submitFeedbackMutation.isPending}
                >
                  {submitFeedbackMutation.isPending ? "Submitting..." : "Submit Feedback"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rating" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Rate Your Experience</CardTitle>
              <CardDescription>
                How satisfied are you with different aspects of Bloom? Your ratings help us prioritize improvements.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleRatingSubmit} className="space-y-6">
                <div className="space-y-6">
                  <div className="text-center space-y-4">
                    <div>
                      <Label className="text-lg font-medium">Overall Experience *</Label>
                      <p className="text-sm text-neutral-600 mb-3">
                        How would you rate your overall experience with Bloom?
                      </p>
                      <div className="flex justify-center">
                        {renderStarRating(
                          ratingData.overallRating, 
                          (rating) => setRatingData(prev => ({ ...prev, overallRating: rating })),
                          "lg"
                        )}
                      </div>
                    </div>
                  </div>

                  <Separator />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label className="font-medium">Ease of Use</Label>
                      <p className="text-sm text-neutral-600 mb-2">How intuitive and user-friendly is the app?</p>
                      {renderStarRating(
                        ratingData.easeOfUse, 
                        (rating) => setRatingData(prev => ({ ...prev, easeOfUse: rating }))
                      )}
                    </div>

                    <div>
                      <Label className="font-medium">Features</Label>
                      <p className="text-sm text-neutral-600 mb-2">Are the features useful and complete?</p>
                      {renderStarRating(
                        ratingData.features, 
                        (rating) => setRatingData(prev => ({ ...prev, features: rating }))
                      )}
                    </div>

                    <div>
                      <Label className="font-medium">Performance</Label>
                      <p className="text-sm text-neutral-600 mb-2">How fast and responsive is the app?</p>
                      {renderStarRating(
                        ratingData.performance, 
                        (rating) => setRatingData(prev => ({ ...prev, performance: rating }))
                      )}
                    </div>

                    <div>
                      <Label className="font-medium">Design</Label>
                      <p className="text-sm text-neutral-600 mb-2">How appealing is the visual design?</p>
                      {renderStarRating(
                        ratingData.design, 
                        (rating) => setRatingData(prev => ({ ...prev, design: rating }))
                      )}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="rating-comments">Additional Comments</Label>
                    <Textarea
                      id="rating-comments"
                      placeholder="Tell us more about your experience, what you love, or what could be improved..."
                      value={ratingData.comments}
                      onChange={(e) => setRatingData(prev => ({ ...prev, comments: e.target.value }))}
                      rows={4}
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={submitRatingMutation.isPending}
                >
                  {submitRatingMutation.isPending ? "Submitting..." : "Submit Rating"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="contact" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Get in Touch</CardTitle>
                <CardDescription>
                  Need direct support or have questions? Here's how to reach us.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <i className="fas fa-envelope text-blue-600"></i>
                  </div>
                  <div>
                    <p className="font-medium">Email Support</p>
                    <p className="text-sm text-neutral-600">Daniel@bloomscouting.com</p>
                    <p className="text-sm text-neutral-600">gfmorone@berkeley.edu</p>
                    <p className="text-sm text-neutral-600">lucas.camargo@berkeley.edu</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <i className="fas fa-clock text-green-600"></i>
                  </div>
                  <div>
                    <p className="font-medium">Response Time</p>
                    <p className="text-sm text-neutral-600">Usually within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                    <i className="fas fa-book text-purple-600"></i>
                  </div>
                  <div>
                    <p className="font-medium">Documentation</p>
                    <div className="text-sm text-neutral-600 space-y-1">
                      <p>
                        <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                          Privacy Policy
                        </a>
                      </p>
                      <p>
                        <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                          Terms of Service
                        </a>
                      </p>
                      <p>
                        <a href="/ferpa-compliance" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                          FERPA Compliance
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Quick Tips</CardTitle>
                <CardDescription>
                  Get the most out of your feedback submissions.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center mt-0.5">
                      <i className="fas fa-lightbulb text-yellow-600 text-xs"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Be Specific</p>
                      <p className="text-xs text-neutral-600">Detailed descriptions help us understand and fix issues faster.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mt-0.5">
                      <i className="fas fa-camera text-blue-600 text-xs"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Include Screenshots</p>
                      <p className="text-xs text-neutral-600">Upload screenshots directly in the feedback form for visual issues and bug reports.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mt-0.5">
                      <i className="fas fa-list text-green-600 text-xs"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Steps Matter</p>
                      <p className="text-xs text-neutral-600">For bugs, include step-by-step reproduction instructions.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center mt-0.5">
                      <i className="fas fa-exclamation text-red-600 text-xs"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Set Priority</p>
                      <p className="text-xs text-neutral-600">Critical issues are prioritized for immediate attention.</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="history" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Your Feedback History</CardTitle>
              <CardDescription>
                View your past feedback submissions and ratings.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingHistory ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  <span className="ml-2">Loading your feedback history...</span>
                </div>
              ) : feedbackHistory ? (
                <div className="space-y-6">
                  {/* User's Rating */}
                  {feedbackHistory.rating && (
                    <div className="border rounded-lg p-4 bg-blue-50">
                      <h3 className="font-medium mb-3 flex items-center gap-2">
                        <i className="fas fa-star text-yellow-500"></i>
                        Your App Rating
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-medium">Overall: </span>
                          <span>{feedbackHistory.rating.overall_rating}/5</span>
                        </div>
                        {feedbackHistory.rating.ease_of_use && (
                          <div>
                            <span className="font-medium">Ease of Use: </span>
                            <span>{feedbackHistory.rating.ease_of_use}/5</span>
                          </div>
                        )}
                        {feedbackHistory.rating.features && (
                          <div>
                            <span className="font-medium">Features: </span>
                            <span>{feedbackHistory.rating.features}/5</span>
                          </div>
                        )}
                        {feedbackHistory.rating.performance && (
                          <div>
                            <span className="font-medium">Performance: </span>
                            <span>{feedbackHistory.rating.performance}/5</span>
                          </div>
                        )}
                        {feedbackHistory.rating.design && (
                          <div>
                            <span className="font-medium">Design: </span>
                            <span>{feedbackHistory.rating.design}/5</span>
                          </div>
                        )}
                      </div>
                      {feedbackHistory.rating.comments && (
                        <div className="mt-3">
                          <span className="font-medium">Comments: </span>
                          <p className="text-neutral-600 mt-1">{feedbackHistory.rating.comments}</p>
                        </div>
                      )}
                      <p className="text-xs text-neutral-500 mt-2">
                        Submitted: {new Date(feedbackHistory.rating.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  )}

                  {/* Feedback Submissions */}
                  {feedbackHistory.feedback && feedbackHistory.feedback.length > 0 ? (
                    <div>
                      <h3 className="font-medium mb-3 flex items-center gap-2">
                        <i className="fas fa-comment-alt text-blue-500"></i>
                        Your Feedback Submissions ({feedbackHistory.feedback.length})
                      </h3>
                      <div className="space-y-3">
                        {feedbackHistory.feedback.map((item: any) => (
                          <div key={item.id} className="border rounded-lg p-4">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Badge
                                  variant={
                                    item.type === "bug" ? "destructive" :
                                    item.type === "feature" ? "default" :
                                    "secondary"
                                  }
                                >
                                  {FEEDBACK_TYPES.find(t => t.value === item.type)?.label || item.type}
                                </Badge>
                                <Badge
                                  variant={
                                    item.priority === "critical" ? "destructive" :
                                    item.priority === "high" ? "destructive" :
                                    item.priority === "medium" ? "default" :
                                    "secondary"
                                  }
                                >
                                  {item.priority.toUpperCase()}
                                </Badge>
                                <Badge
                                  variant={
                                    item.status === "resolved" ? "default" :
                                    item.status === "in_progress" ? "default" :
                                    "secondary"
                                  }
                                >
                                  {item.status.replace("_", " ").toUpperCase()}
                                </Badge>
                              </div>
                              <span className="text-xs text-neutral-500">
                                {new Date(item.created_at).toLocaleDateString()}
                              </span>
                            </div>
                            <h4 className="font-medium mb-1">{item.subject}</h4>
                            <p className="text-sm text-neutral-600 mb-2">{item.description}</p>
                            {item.steps_to_reproduce && (
                              <div className="text-xs">
                                <span className="font-medium">Steps to reproduce: </span>
                                <span className="text-neutral-600">{item.steps_to_reproduce}</span>
                              </div>
                            )}
                            {item.page_url && (
                              <p className="text-xs text-neutral-500 mt-1">
                                Page: {item.page_url}
                              </p>
                            )}
                            {item.screenshot_urls && item.screenshot_urls.length > 0 && (
                              <div className="mt-2">
                                <p className="text-xs font-medium text-neutral-700 mb-1">
                                  Screenshots ({item.screenshot_urls.length}):
                                </p>
                                <div className="flex flex-wrap gap-2">
                                  {item.screenshot_urls.map((url: string, index: number) => (
                                    <a
                                      key={index}
                                      href={url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs text-blue-600 hover:underline"
                                    >
                                      📷 Screenshot {index + 1}
                                    </a>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-neutral-500">
                      <i className="fas fa-comment-slash text-3xl mb-2"></i>
                      <p>No feedback submissions yet.</p>
                      <p className="text-sm">Switch to the "Report Issue / Feedback" tab to submit your first feedback!</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-neutral-500">
                  <p>Unable to load feedback history.</p>
                  <Button 
                    variant="outline" 
                    onClick={() => window.location.reload()}
                    className="mt-2"
                  >
                    Retry
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
} 